import React, { useEffect, useState, useRef } from "react";
import { api, formatError } from "../../lib/api.js";
import { Plus, Trash2, Power, PowerOff, ChevronRight, ChevronDown, Car, Edit3, Check, X as XIcon, Download, Upload } from "lucide-react";
import { Note, inputCls } from "./_shared.jsx";
import { invalidateVehicleCatalogue } from "../../lib/vehicleCatalogue.jsx";

export default function VehicleCataloguePanel() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedBrand, setExpandedBrand] = useState(null);
  const [expandedModel, setExpandedModel] = useState(null);
  const [error, setError] = useState("");
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vehicle-catalogue?include_inactive=true");
      setBrands(data);
      invalidateVehicleCatalogue(); // bust the picker cache so new entries show in dropdowns
    } catch (e) {
      setError(formatError(e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleExport = async () => {
    try {
      const resp = await api.get("/vehicle-catalogue/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "vehicle_catalogue_export.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatError(e.response?.data?.detail || e.message));
    }
  };

  const handleFilePick = () => fileInputRef.current?.click();

  const runImport = async (file, dryRun) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("dry_run", dryRun ? "true" : "false");
    try {
      const { data } = await api.post("/vehicle-catalogue/import", fd);
      setImportResult({ ...data, filename: file.name });
      if (!dryRun) await load();
      return data;
    } catch (e) {
      setError(formatError(e.response?.data?.detail || e.message));
      return null;
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportResult(null);
    setError("");
    const data = await runImport(file, true);
    // Reset input so same file can be re-picked after
    e.target.value = "";
    // Stash the file so a Confirm button can commit it
    if (data) {
      window._pendingCatalogueFile = file;
    }
  };

  const commitImport = async () => {
    const file = window._pendingCatalogueFile;
    if (!file) return;
    await runImport(file, false);
    window._pendingCatalogueFile = null;
  };

  return (
    <div data-testid="vehicle-catalogue-panel">
      <div className="mb-3 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-base font-bold text-[#1A1A2E] flex items-center gap-2">
            <Car className="w-4 h-4 text-[#1F3864]" />
            Catálogo de Viaturas
          </h2>
          <div className="text-xs text-[#6C757D] mt-0.5">
            Gere a hierarquia <b>Marca → Modelo → Geração</b>. As gerações aparecem como dropdown nos formulários de VR, SOM, BPS, VRQ, PEB e PEC — e separam dados equivalentes (ex.: BT-50 UR vs TF) para analytics correctos.
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleExport}
            data-testid="catalogue-export-btn"
            className="text-xs border border-gray-300 hover:bg-[#F4F6F8] rounded-sm px-3 py-1.5 flex items-center gap-1"
          >
            <Download className="w-3 h-3" /> Exportar Excel
          </button>
          <button
            onClick={handleFilePick}
            data-testid="catalogue-import-btn"
            className="text-xs bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm px-3 py-1.5 flex items-center gap-1"
          >
            <Upload className="w-3 h-3" /> Importar Excel
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            onChange={handleFileChange}
            className="hidden"
            data-testid="catalogue-import-file"
          />
        </div>
      </div>

      {importResult && (
        <div className="mb-3 border border-[#1F3864]/40 bg-[#F0F4FA] rounded-sm p-3 text-xs" data-testid="catalogue-import-result">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold text-[#1A1A2E]">
              {importResult.dry_run ? "Pré-visualização" : "Importação aplicada"} — {importResult.filename}
            </div>
            <button onClick={() => setImportResult(null)} className="text-[#6C757D] hover:text-[#1A1A2E]"><XIcon className="w-3 h-3" /></button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
            <Stat label="Marcas novas" value={importResult.brands_added} />
            <Stat label="Modelos novos" value={importResult.models_added} />
            <Stat label="Gerações novas" value={importResult.generations_added} />
            <Stat label="Gerações actualizadas" value={importResult.generations_updated} />
            <Stat label="Linhas lidas" value={importResult.total_rows_parsed} />
            <Stat label="Sem alterações" value={importResult.skipped} />
          </div>
          {importResult.errors?.length > 0 && (
            <div className="bg-[#F8D7DA] text-[#721C24] rounded-sm px-2 py-1 mb-2">
              <b>Avisos ({importResult.errors.length}):</b>
              <ul className="list-disc list-inside">
                {importResult.errors.slice(0, 5).map((er, i) => (<li key={i}>{er}</li>))}
                {importResult.errors.length > 5 && <li>… +{importResult.errors.length - 5} mais</li>}
              </ul>
            </div>
          )}
          {importResult.dry_run && (
            <div className="flex items-center gap-2">
              <button
                onClick={commitImport}
                data-testid="catalogue-import-confirm"
                className="text-xs bg-[#28A745] hover:bg-[#1E7E34] text-white rounded-sm px-3 py-1.5 font-medium flex items-center gap-1"
              >
                <Check className="w-3 h-3" /> Confirmar e aplicar
              </button>
              <button
                onClick={() => { setImportResult(null); window._pendingCatalogueFile = null; }}
                className="text-xs border border-gray-300 hover:bg-[#F4F6F8] rounded-sm px-3 py-1.5"
              >
                Cancelar
              </button>
            </div>
          )}
        </div>
      )}

      {error && <div className="px-3 py-2 mb-3 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm">{error}</div>}

      {loading ? (
        <div className="p-6 text-center text-sm text-[#6C757D]">A carregar…</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-wider text-[#6C757D]">
              <tr>
                <th className="px-3 py-2 text-left">Marca</th>
                <th className="px-3 py-2 text-right w-32">Modelos</th>
                <th className="px-3 py-2 text-right w-32">Gerações</th>
                <th className="px-3 py-2 w-10" />
              </tr>
            </thead>
            <tbody>
              {brands.length === 0 && (
                <tr><td colSpan="4" className="px-3 py-6 text-center text-xs text-[#6C757D]">Ainda sem catálogo. Cria uma marca na aba <b>Marcas e Dealers</b> primeiro.</td></tr>
              )}
              {brands.map((b) => (
                <React.Fragment key={b.id}>
                  <tr className="border-t border-gray-100 cursor-pointer hover:bg-[#F4F6F8]" onClick={() => setExpandedBrand(expandedBrand === b.id ? null : b.id)} data-testid={`brand-row-${b.id}`}>
                    <td className="px-3 py-2 font-semibold text-[#1A1A2E]">{b.brand}</td>
                    <td className="px-3 py-2 text-right font-mono text-[#1F3864]">{b.models.length}</td>
                    <td className="px-3 py-2 text-right font-mono text-[#1F3864]">
                      {b.models.reduce((n, m) => n + m.generations.length, 0)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {expandedBrand === b.id ? <ChevronDown className="w-4 h-4 inline" /> : <ChevronRight className="w-4 h-4 inline" />}
                    </td>
                  </tr>
                  {expandedBrand === b.id && (
                    <tr>
                      <td colSpan="4" className="p-0 bg-[#F8FAFC]">
                        <ModelsEditor
                          brandId={b.id}
                          brandName={b.brand}
                          models={b.models}
                          expandedModel={expandedModel}
                          setExpandedModel={setExpandedModel}
                          onChanged={load}
                          onError={setError}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Note tone="info">
        <b>Renomear</b> um modelo/geração actualiza automaticamente todos os registos históricos (SOMs, BPS, VRs, PEBs) para manter os filtros consistentes.{" "}
        <b>Desactivar</b> esconde dos dropdowns mas mantém o histórico — é reversível.
      </Note>
    </div>
  );
}


function ModelsEditor({ brandId, brandName, models, expandedModel, setExpandedModel, onChanged, onError }) {
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");

  const addModel = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await api.post(`/brands/${brandId}/models`, { name });
      setNewName("");
      onChanged();
    } catch (e) {
      onError(formatError(e.response?.data?.detail));
    }
  };

  const saveRename = async (m) => {
    const name = editingName.trim();
    if (!name || name === m.name) { setEditingId(null); return; }
    try {
      await api.patch(`/brands/${brandId}/models/${m.id}`, { name });
      setEditingId(null);
      onChanged();
    } catch (e) {
      onError(formatError(e.response?.data?.detail));
    }
  };

  const toggleActive = async (m) => {
    try {
      await api.patch(`/brands/${brandId}/models/${m.id}`, { active: !m.active });
      onChanged();
    } catch (e) { onError(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <input
          placeholder="Nome do novo modelo"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addModel()}
          className={inputCls + " max-w-xs"}
          data-testid={`new-model-input-${brandId}`}
        />
        <button
          onClick={addModel}
          disabled={!newName.trim()}
          className="bg-[#1F3864] hover:bg-[#142542] text-white px-3 py-1.5 rounded-sm text-sm flex items-center gap-1 disabled:opacity-60"
          data-testid={`new-model-btn-${brandId}`}
        >
          <Plus className="w-3 h-3" /> Adicionar modelo
        </button>
      </div>

      {models.length === 0 ? (
        <div className="text-xs text-[#6C757D] py-2">Sem modelos ainda — adiciona o primeiro acima.</div>
      ) : (
        <div className="space-y-2">
          {models.map((m) => (
            <div key={m.id} className={`border border-gray-200 rounded-md bg-white ${!m.active ? "opacity-60" : ""}`} data-testid={`model-row-${m.id}`}>
              <div className="px-3 py-2 flex items-center gap-2">
                {editingId === m.id ? (
                  <>
                    <input autoFocus value={editingName} onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") saveRename(m); if (e.key === "Escape") setEditingId(null); }}
                      className={inputCls + " max-w-xs"} data-testid={`model-edit-${m.id}`} />
                    <button onClick={() => saveRename(m)} className="text-[#155724] hover:underline text-xs flex items-center gap-1"><Check className="w-3 h-3" /> Guardar</button>
                    <button onClick={() => setEditingId(null)} className="text-[#6C757D] hover:underline text-xs flex items-center gap-1"><XIcon className="w-3 h-3" /> Cancelar</button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => setExpandedModel(expandedModel === m.id ? null : m.id)}
                      className="font-semibold text-[#1A1A2E] flex items-center gap-1"
                      data-testid={`model-expand-${m.id}`}
                    >
                      {expandedModel === m.id ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                      {m.name}
                    </button>
                    <span className="text-xs text-[#6C757D]">{m.generations.length} geração{m.generations.length === 1 ? "" : "ões"}</span>
                    {!m.active && <span className="text-[10px] bg-gray-200 text-gray-600 px-1.5 rounded-sm">Desactivado</span>}
                    <div className="ml-auto flex items-center gap-3 text-xs">
                      <button onClick={() => { setEditingId(m.id); setEditingName(m.name); }} className="text-[#1F3864] hover:underline flex items-center gap-1" data-testid={`model-rename-${m.id}`}>
                        <Edit3 className="w-3 h-3" /> Renomear
                      </button>
                      <button onClick={() => toggleActive(m)} className={`${m.active ? "text-[#856404]" : "text-[#155724]"} hover:underline flex items-center gap-1`} data-testid={`model-toggle-${m.id}`}>
                        {m.active ? <><PowerOff className="w-3 h-3" /> Desactivar</> : <><Power className="w-3 h-3" /> Reactivar</>}
                      </button>
                    </div>
                  </>
                )}
              </div>
              {expandedModel === m.id && (
                <GenerationsEditor
                  brandId={brandId}
                  brandName={brandName}
                  model={m}
                  onChanged={onChanged}
                  onError={onError}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function GenerationsEditor({ brandId, brandName, model, onChanged, onError }) {
  const [n, setN] = useState("");
  const [yf, setYf] = useState("");
  const [yt, setYt] = useState("");

  const add = async () => {
    const name = n.trim();
    if (!name) return;
    try {
      await api.post(`/brands/${brandId}/models/${model.id}/generations`, {
        name,
        year_from: yf ? parseInt(yf, 10) : null,
        year_to: yt ? parseInt(yt, 10) : null,
      });
      setN(""); setYf(""); setYt("");
      onChanged();
    } catch (e) {
      onError(formatError(e.response?.data?.detail));
    }
  };

  const toggleActive = async (g) => {
    try {
      await api.patch(`/brands/${brandId}/models/${model.id}/generations/${g.id}`, { active: !g.active });
      onChanged();
    } catch (e) { onError(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="border-t border-gray-100 bg-[#F8FAFC] px-3 py-2">
      <div className="flex items-center gap-2 mb-2">
        <input placeholder="Nome geração (ex.: UR, F25)" value={n} onChange={(e) => setN(e.target.value)}
          className={inputCls + " w-40"} data-testid={`new-gen-name-${model.id}`} />
        <input placeholder="Ano início" type="number" value={yf} onChange={(e) => setYf(e.target.value)}
          className={inputCls + " w-24"} data-testid={`new-gen-yf-${model.id}`} />
        <input placeholder="Ano fim" type="number" value={yt} onChange={(e) => setYt(e.target.value)}
          className={inputCls + " w-24"} data-testid={`new-gen-yt-${model.id}`} />
        <button onClick={add} disabled={!n.trim()}
          className="bg-[#1F3864] hover:bg-[#142542] text-white px-3 py-1 rounded-sm text-xs flex items-center gap-1 disabled:opacity-60"
          data-testid={`new-gen-btn-${model.id}`}>
          <Plus className="w-3 h-3" /> Adicionar geração
        </button>
      </div>
      {model.generations.length === 0 ? (
        <div className="text-xs text-[#6C757D] py-1">Sem gerações ainda.</div>
      ) : (
        <ul className="space-y-1">
          {model.generations.map((g) => (
            <li key={g.id} className={`flex items-center gap-2 text-xs border border-gray-200 rounded-sm px-2 py-1 bg-white ${!g.active ? "opacity-60" : ""}`} data-testid={`gen-row-${g.id}`}>
              <span className="font-mono text-[#1A1A2E]">{g.name}</span>
              <span className="text-[#6C757D]">
                {g.year_from || "?"} – {g.year_to || "actual"}
              </span>
              {!g.active && <span className="text-[10px] bg-gray-200 text-gray-600 px-1.5 rounded-sm">Desactivada</span>}
              <button onClick={() => toggleActive(g)} className={`ml-auto ${g.active ? "text-[#856404]" : "text-[#155724]"} hover:underline flex items-center gap-1`} data-testid={`gen-toggle-${g.id}`}>
                {g.active ? <><PowerOff className="w-3 h-3" /> Desactivar</> : <><Power className="w-3 h-3" /> Reactivar</>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function Stat({ label, value }) {
  return (
    <div className="bg-white border border-gray-200 rounded-sm px-2 py-1">
      <div className="text-[10px] text-[#6C757D] uppercase tracking-wider">{label}</div>
      <div className="text-sm font-mono text-[#1F3864]">{value ?? 0}</div>
    </div>
  );
}
